import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button, Select } from '../../components/ui';
import { SearchIcon, PrinterIcon } from '../../components/ui/Icons';
import {
  TimelineHourHeader,
  TimelinePaintGrid,
  formatHm,
  paintSegment,
  type TimeSegment,
} from '../../components/timeline-grid';
import { fetcher } from '../../services/fetcher';
import { putWrhArrangements } from '../../services/mutations';

const PROFILES = [
  {
    id: 'WATCHKEEPER_AT_SEA',
    label: 'Watchkeeper · At sea',
    short: 'WK · Sea',
    place: 'At sea',
    role: 'Watchkeeper',
  },
  {
    id: 'WATCHKEEPER_IN_PORT',
    label: 'Watchkeeper · In port',
    short: 'WK · Port',
    place: 'In port',
    role: 'Watchkeeper',
  },
  {
    id: 'NON_WATCHKEEPER_AT_SEA',
    label: 'Non-W · At sea',
    short: 'NW · Sea',
    place: 'At sea',
    role: 'Non-watchkeeper',
  },
  {
    id: 'NON_WATCHKEEPER_IN_PORT',
    label: 'Non-W · In port',
    short: 'NW · Port',
    place: 'In port',
    role: 'Non-watchkeeper',
  },
] as const;

function sortedSegments(segments: TimeSegment[]): TimeSegment[] {
  return [...segments].sort((a, b) => a.startMinute - b.startMinute);
}

function formatRange(seg: TimeSegment): string {
  return `${formatHm(seg.startMinute)}–${formatHm(seg.endMinute)}`;
}

function totalWorkHours(segments: TimeSegment[]): number {
  return segments.reduce((sum, s) => sum + Math.max(0, s.endMinute - s.startMinute), 0) / 60;
}

type ProfileRow = {
  profile: string;
  notes: string | null;
  segments: TimeSegment[];
};

type CrewRow = {
  crewId: number;
  crewName: string;
  rankName?: string | null;
  watchkeeper: boolean;
  profiles: ProfileRow[];
};

type Sheet = { vesselId: number; vesselName: string; crew: CrewRow[] };

const SLOT_W = 26;
const HOURS_W = 48 * SLOT_W;
const LABEL_W = 112;
const CELL_H = 30;

const ArrangementsPage: React.FC<{ lockedVesselId?: number }> = ({ lockedVesselId }) => {
  const { colors } = useTheme();
  const { data: lookups } = useSWR<{ vessels: Array<{ id: number; vesselName: string }> }>(
    '/crew/lookups',
    fetcher,
    { revalidateOnFocus: false },
  );
  const [vesselId, setVesselId] = useState(lockedVesselId ? String(lockedVesselId) : '');
  const [applied, setApplied] = useState<string | null>(
    lockedVesselId ? String(lockedVesselId) : null,
  );
  const [selectedCrewId, setSelectedCrewId] = useState<number | null>(null);

  const key = applied ? `/wrh/arrangements?vesselId=${applied}` : null;
  const { data, mutate, isLoading } = useSWR<Sheet>(key, fetcher, { revalidateOnFocus: false });

  const vesselOptions = useMemo(
    () => (lookups?.vessels ?? []).map((v) => ({ value: String(v.id), label: v.vesselName })),
    [lookups?.vessels],
  );

  const selectedCrew = useMemo(() => {
    if (!data?.crew?.length) return null;
    return data.crew.find((c) => c.crewId === selectedCrewId) ?? data.crew[0];
  }, [data, selectedCrewId]);

  const saveProfiles = async (
    crew: CrewRow,
    patch: { profile: string; segments?: TimeSegment[]; notes?: string | null },
  ) => {
    if (!applied) return;
    const profiles = crew.profiles.map((p) => {
      const isTarget = p.profile === patch.profile;
      return {
        profile: p.profile,
        segments: isTarget && patch.segments !== undefined ? patch.segments : p.segments,
        notes: isTarget && patch.notes !== undefined ? patch.notes : (p.notes ?? null),
      };
    });
    await putWrhArrangements({
      vesselId: Number(applied),
      crewId: crew.crewId,
      profiles,
    });
    await mutate();
  };

  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-2', colors.background)}>
      <div
        className={cn(
          'flex shrink-0 flex-wrap items-center gap-2 rounded-xl border px-3 py-2',
          colors.border,
          colors.surface,
        )}
      >
        {!lockedVesselId && (
          <div className="w-[11rem]">
            <Select
              value={vesselId}
              onChange={(e) => setVesselId(e.target.value)}
              options={vesselOptions}
              placeholder="Vessel"
              showClearButton={false}
            />
          </div>
        )}
        <Button
          type="button"
          size="sm"
          onClick={() => {
            const vid = lockedVesselId ? String(lockedVesselId) : vesselId;
            if (!vid) return;
            setApplied(vid);
            setSelectedCrewId(null);
          }}
        >
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>

        {data && (
          <>
            <div className={cn('mx-1 hidden h-6 w-px sm:block', 'bg-black/10 dark:bg-white/10')} />
            <div className="min-w-0 flex-1">
              {selectedCrew ? (
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className={cn('truncate text-sm font-semibold', colors.text)}>
                    {selectedCrew.crewName}
                  </span>
                  <span className={cn('truncate text-xs', colors.textMuted)}>
                    {selectedCrew.rankName || '—'}
                    {selectedCrew.watchkeeper ? ' · WK' : ''}
                  </span>
                </div>
              ) : (
                <span className={cn('text-xs', colors.textMuted)}>Select a crew member</span>
              )}
            </div>
          </>
        )}

        {data && applied ? (
          <Link to={`/wrh/arrangements/report?vesselId=${encodeURIComponent(applied)}`} className="ml-auto shrink-0">
            <Button type="button" size="sm" variant="secondary">
              <PrinterIcon className="h-4 w-4" />
              Print
            </Button>
          </Link>
        ) : null}
      </div>

      {isLoading && (
        <p className={cn('shrink-0 text-sm', colors.textMuted)}>Loading arrangements…</p>
      )}

      {!applied && !isLoading && (
        <div
          className={cn(
            'flex flex-1 items-center justify-center rounded-xl border border-dashed text-sm',
            colors.border,
            colors.textMuted,
          )}
        >
          Choose vessel, then Search.
        </div>
      )}

      {data && (
        <div className="flex min-h-0 flex-1 gap-2">
          <aside
            className={cn(
              'flex w-44 shrink-0 flex-col overflow-hidden rounded-xl border sm:w-52',
              colors.border,
              colors.surface,
            )}
          >
            <div
              className={cn(
                'flex shrink-0 items-center justify-between border-b px-2.5 py-1.5',
                colors.border,
              )}
            >
              <span
                className={cn(
                  'text-[10px] font-semibold uppercase tracking-wide',
                  colors.textSecondary,
                )}
              >
                Crew
              </span>
              <span className={cn('text-[10px] tabular-nums', colors.textMuted)}>
                {data.crew.length}
              </span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-1">
              {data.crew.map((c) => {
                const active = selectedCrew?.crewId === c.crewId;
                return (
                  <button
                    key={c.crewId}
                    type="button"
                    className={cn(
                      'mb-0.5 flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left transition-colors',
                      active
                        ? 'bg-sky-500/15 ring-1 ring-sky-500/30'
                        : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.04]',
                      colors.text,
                    )}
                    onClick={() => setSelectedCrewId(c.crewId)}
                  >
                    <span
                      className={cn(
                        'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
                        c.watchkeeper ? 'bg-sky-500' : 'bg-zinc-400',
                      )}
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium leading-tight">
                        {c.crewName}
                      </span>
                      <span className={cn('block truncate text-[10px]', colors.textMuted)}>
                        {c.rankName || '—'}
                        {c.watchkeeper ? ' · WK' : ''}
                      </span>
                    </span>
                  </button>
                );
              })}
              {data.crew.length === 0 && (
                <p className={cn('px-2 py-4 text-xs', colors.textMuted)}>
                  No onboard crew found for this vessel.
                </p>
              )}
            </div>
            <div
              className={cn(
                'flex shrink-0 gap-2.5 border-t px-2.5 py-1.5 text-[10px]',
                colors.border,
                colors.textMuted,
              )}
            >
              <span className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-sky-500" /> WK
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" /> Non-W
              </span>
            </div>
          </aside>

          <div
            className={cn(
              'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border',
              colors.border,
              colors.surface,
            )}
          >
            {!selectedCrew ? (
              <div className={cn('flex flex-1 items-center justify-center text-sm', colors.textMuted)}>
                Select a crew member to edit arrangements.
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 overflow-auto">
                  <div
                    className={cn(
                      'sticky top-0 z-10 flex min-w-max items-stretch border-b bg-white/95 backdrop-blur-sm dark:bg-zinc-950/95',
                      colors.border,
                    )}
                  >
                    <div
                      className={cn(
                        'sticky left-0 z-20 flex shrink-0 items-end px-2 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wide',
                        colors.textSecondary,
                        'bg-white/95 dark:bg-zinc-950/95',
                      )}
                      style={{ width: LABEL_W }}
                    >
                      Profile
                    </div>
                    <div className="min-w-0 flex-1 px-1 pb-1 pt-1.5" style={{ minWidth: HOURS_W }}>
                      <TimelineHourHeader slotMinutes={30} fillWidth cellMinWidth={SLOT_W} />
                    </div>
                  </div>

                  {PROFILES.map((meta, idx) => {
                    const row = selectedCrew.profiles.find((p) => p.profile === meta.id) ?? {
                      profile: meta.id,
                      notes: null as string | null,
                      segments: [] as TimeSegment[],
                    };
                    return (
                      <div
                        key={meta.id}
                        className={cn(
                          'flex min-w-max items-stretch border-b border-black/[0.04] dark:border-white/[0.04]',
                          idx % 2 === 1 && 'bg-black/[0.015] dark:bg-white/[0.02]',
                        )}
                      >
                        <div
                          className={cn(
                            'sticky left-0 z-[5] flex shrink-0 flex-col justify-center px-2 py-0.5',
                            colors.surface,
                          )}
                          style={{ width: LABEL_W }}
                          title={meta.label}
                        >
                          <div
                            className={cn('truncate text-[12px] font-medium leading-tight', colors.text)}
                          >
                            {meta.short}
                          </div>
                          <div className={cn('truncate text-[10px]', colors.textMuted)}>
                            {meta.label}
                          </div>
                        </div>
                        <div className="min-w-0 flex-1 px-1 py-0.5" style={{ minWidth: HOURS_W }}>
                          <TimelinePaintGrid
                            slotMinutes={30}
                            fillWidth
                            cellMinWidth={SLOT_W}
                            cellHeight={CELL_H}
                            segments={row.segments}
                            fillColor="rgb(37 99 235)"
                            fillLabel="W"
                            menuOptions={[{ id: 'W', label: 'Work (W)' }]}
                            onPaint={(range, optionId) => {
                              const next = paintSegment(
                                row.segments,
                                range.startMinute,
                                range.endMinute,
                                optionId
                                  ? {
                                      startMinute: range.startMinute,
                                      endMinute: range.endMinute,
                                    }
                                  : null,
                              );
                              void saveProfiles(selectedCrew, { profile: meta.id, segments: next });
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div
                  className={cn(
                    'shrink-0 border-t px-3 py-3',
                    colors.border,
                    'bg-zinc-50/80 dark:bg-zinc-900/80',
                  )}
                >
                  <div className="mb-2 flex items-baseline justify-between gap-2">
                    <span
                      className={cn(
                        'text-[10px] font-semibold uppercase tracking-wide',
                        colors.textSecondary,
                      )}
                    >
                      Summary
                    </span>
                    <span className={cn('text-[10px]', colors.textMuted)}>
                      {selectedCrew.crewName}
                    </span>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {PROFILES.map((meta) => {
                      const row = selectedCrew.profiles.find((p) => p.profile === meta.id) ?? {
                        profile: meta.id,
                        notes: null as string | null,
                        segments: [] as TimeSegment[],
                      };
                      const segs = sortedSegments(row.segments);
                      const hours = totalWorkHours(segs);
                      const empty = segs.length === 0;
                      return (
                        <div
                          key={meta.id}
                          className={cn(
                            'rounded-lg border px-2.5 py-2',
                            colors.border,
                            colors.surface,
                            empty && !row.notes && 'opacity-70',
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span
                                  className={cn(
                                    'inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold',
                                    meta.role === 'Watchkeeper'
                                      ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
                                      : 'bg-zinc-500/15 text-zinc-600 dark:text-zinc-300',
                                  )}
                                >
                                  {meta.short}
                                </span>
                                <span className={cn('text-[11px]', colors.textMuted)}>
                                  {meta.place} · {meta.role}
                                </span>
                              </div>
                            </div>
                            <span
                              className={cn(
                                'shrink-0 tabular-nums text-[11px] font-medium',
                                empty ? colors.textMuted : 'text-sky-700 dark:text-sky-300',
                              )}
                            >
                              {empty ? '0h' : `${hours % 1 === 0 ? hours : hours.toFixed(1)}h`}
                            </span>
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {empty ? (
                              <span className={cn('text-[11px] italic', colors.textMuted)}>
                                No work periods set
                              </span>
                            ) : (
                              segs.map((s) => (
                                <span
                                  key={`${s.startMinute}-${s.endMinute}`}
                                  className={cn(
                                    'inline-flex items-center rounded-md border px-1.5 py-0.5',
                                    'text-[11px] font-medium tabular-nums',
                                    'border-sky-500/25 bg-sky-500/10 text-sky-800 dark:text-sky-200',
                                  )}
                                >
                                  {formatRange(s)}
                                </span>
                              ))
                            )}
                          </div>
                          <input
                            type="text"
                            key={`${meta.id}:${row.notes ?? ''}`}
                            defaultValue={row.notes ?? ''}
                            maxLength={500}
                            placeholder="Notes…"
                            onBlur={(e) => {
                              const next = e.target.value.trim() || null;
                              if (next !== (row.notes || null)) {
                                void saveProfiles(selectedCrew, {
                                  profile: meta.id,
                                  notes: next,
                                });
                              }
                            }}
                            className={cn(
                              'mt-2 h-7 w-full rounded-md border bg-transparent px-2 text-[11px] outline-none transition-colors',
                              'placeholder:text-zinc-400 dark:placeholder:text-zinc-500',
                              'hover:bg-black/[0.03] focus:bg-white focus:ring-1 focus:ring-sky-500/35 dark:hover:bg-white/[0.04] dark:focus:bg-zinc-900',
                              colors.border,
                              colors.text,
                            )}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArrangementsPage;
